import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from "@angular/core";
import * as _ from "lodash";
import { Constants } from "../../../meeting-manager/lib/constants";
import { MeetingTypeList } from "app/meeting-manager/lib/models/meeting-type-model.model";
import { IAutocompleteComponent, IAutocompleteOption, IListItemComponent, isOptionType, optionEqualPredicate, OptionType } from "@tylertech/forge";

@Component({
  selector: "appc-meeting-type-selection",
  templateUrl: "./meeting-type-selection.component.html",
  styleUrl: "./meeting-type-selection.component.scss",
  standalone: false,
})
export class MeetingTypeSelectionComponent implements AfterViewInit, OnChanges {
  @Input() allMeetingTypes: MeetingTypeList[] = [];
  @Input() selectedMeetingTypes: number[] = [];
  meetingTypesText: string = '';
  @ViewChild('meetingTypeAutoCompleteRef') autoCompleteRefs: ElementRef;
  meetingTypeAutoComplete: IAutocompleteComponent;

  @Output() onSelectMeetingType: EventEmitter<number[]> = new EventEmitter<number[]>();
  constructor(public constants: Constants) {}

  ngAfterViewInit () {
    this._initializeAutocompleteMeetingType();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const value = changes.selectedMeetingTypes?.currentValue;
    const selectedAllMeetingTypes = this.allMeetingTypes.map(item => item.id);
    if (this.meetingTypeAutoComplete && value) {
      if (selectedAllMeetingTypes.length == value.length) {
        this.meetingTypeAutoComplete.value = [-1, ...selectedAllMeetingTypes];
      } else {
        this.meetingTypeAutoComplete.value = value;
      }
    }
  }
  
  /**
   * Initializes the Forge Auto Complete Component
   * Overrides the sort options behavior, option builder, and selected text builder.
   */
  private _initializeAutocompleteMeetingType () {
    const autocompleteElement = this.autoCompleteRefs.nativeElement;
    const autocompleteCore = autocompleteElement._core;

    // Custom Multiple Select Sorting to put `Select All` on top
    autocompleteCore._sortSelectedOptions = () => {
      if (autocompleteCore._multiple &&
        autocompleteCore._selectedOptions.length &&
        isOptionType(autocompleteCore._options, OptionType.Option)) {
        const selectedOptions: IAutocompleteOption[] = [];
        const unselectedOptions: IAutocompleteOption[] = [];
        let selectAllOption: IAutocompleteOption;
          autocompleteCore._options.forEach((option: IAutocompleteOption) => {
            if (autocompleteCore._selectedOptions.find((o: IAutocompleteOption) => option.value === -1)) {
              selectAllOption = option;
            } else if (autocompleteCore._selectedOptions.find((o: IAutocompleteOption) => 
              optionEqualPredicate(o, option.value, autocompleteCore._matchKey))) {
                selectedOptions.push(option);
            } else {
                unselectedOptions.push(option);
            }
            if (selectAllOption) {
              autocompleteCore._options = [selectAllOption, ...selectedOptions, ...unselectedOptions];
            } else {
              autocompleteCore._options = [...selectedOptions, ...unselectedOptions];
            }
          });
      } 
    }

    this.meetingTypeAutoComplete = autocompleteElement;
    this.meetingTypeAutoComplete.allowUnmatched = false;
    this.meetingTypeAutoComplete.filterFocusFirst = false;
    this.meetingTypeAutoComplete.filterOnFocus = false;
    this.meetingTypeAutoComplete.multiple = true;
    this.meetingTypeAutoComplete.optionBuilder = (
        option: IAutocompleteOption<any>,
        filterText: string,
        parentElement: IListItemComponent) => {
      const item = document.createElement('span');
      let innerHTML = '';

      if (option.value === -1) {
        innerHTML = `<b> ${option.label} </b>`
      } else {
        const regExp = new RegExp('(' + filterText + ')', 'ig')
        const tokens = option.label.split(regExp);

        for (const token of tokens) {
          innerHTML += filterText.toLowerCase() === token.toLowerCase() ? '<b>' + token + '</b>' : token;
        }
      }

      item.innerHTML = innerHTML;
 
      return item;
    };

    this.meetingTypeAutoComplete.filter = (filterText, _value) => {
      const sortedMeetingTypes = this.allMeetingTypes.sort((m1, m2) => m1.title.localeCompare(m2.title));
      const filteredList = sortedMeetingTypes
        .filter((item) => item.title.toLowerCase().includes(filterText.toLowerCase()))
        .map((item) =>  { return { value: item.id, label: item.title }});

        if (filterText === null || filterText.trim() === "") {
          filteredList.unshift({
            value: -1,
            label: 'Select All'
          })
        }
      return filteredList;
    };

    this.meetingTypeAutoComplete.selectedTextBuilder = (selectedOptions: Array<IAutocompleteOption<number>>) => {
      const selectedMeetingTypes = selectedOptions.map(option => option.value).filter(o => o != -1);
      if (selectedOptions.some(option => option.value === -1) || selectedMeetingTypes.length === this.allMeetingTypes.length) {
        return `All Meeting Types Selected`;
      }

      if (selectedMeetingTypes.length === 0) {
        return `No Meeting Type Selected`;
      }

      return selectedMeetingTypes.length + ` Meeting Types Selected`;
    }

    
    this.meetingTypeAutoComplete.value = this.selectedMeetingTypes;
    if (this.allMeetingTypes.length === this.selectedMeetingTypes.length) {
      this.meetingTypeAutoComplete.value = [-1, ...this.selectedMeetingTypes];
    }
    this.meetingTypeAutoComplete.beforeValueChange = (value: number) => {
      if (this.meetingTypeAutoComplete) {
        if (value === -1 && this.meetingTypeAutoComplete.value.includes(-1)) {
          this.meetingTypeAutoComplete.value = [];
        } else if (value === -1) {
          this.meetingTypeAutoComplete.value = this.allMeetingTypes.map(item => item.id);
        } else {
          this.meetingTypeAutoComplete.value = this.meetingTypeAutoComplete.value.filter((o:number) => o != -1);
        }
      }
      
      return true;
    };
  }

  onChangeMeetingType (event: CustomEvent<number[]>) {
    const allMeetingTypeIds = this.allMeetingTypes.map(o => o.id);
    const filteredSelectedMeetingTypes = this.meetingTypeAutoComplete.value.filter((o:number) => o != -1);
    if (event.detail.includes(-1) && filteredSelectedMeetingTypes.length === allMeetingTypeIds.length) {
      this.meetingTypeAutoComplete.value = [-1, ...filteredSelectedMeetingTypes];
    } else if (event.detail.includes(-1) && filteredSelectedMeetingTypes.length > 0) {
      this.meetingTypeAutoComplete.value = filteredSelectedMeetingTypes;
    } else if (event.detail.includes(-1)) {
      this.meetingTypeAutoComplete.value = [];
    }
  }

  onEmitMeetingType (event: CustomEvent) {
    let emittedValue: number[] = this.meetingTypeAutoComplete.value;
    this.onSelectMeetingType.emit(emittedValue);
  }
}

